// Root layout: providers for overlays + global chrome around the routed page.
import { lazy, Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router';
import { useBodyClass } from '@/app/providers/bodyClass';
import { syncChromeColor } from '@/lib/chrome';
import { useStillPage } from '@/lib/overlay';
import { useVisualViewportController } from '@/lib/visualViewport';
import { ShellCtx } from '@/app/providers/Shell';
import { LanguageGate } from '@/app/providers/Language';
import { AskBar } from '@/features/ai/AskBar';
import { OrcaProvider, useOrca } from '@/features/ai/OrcaProvider';
import { NewsSheetProvider, useNewsSheet } from '@/features/news/NewsSheet';
import { NoticesProvider } from '@/features/notices/NoticesProvider';
import { UrgentStack } from '@/features/notices/UrgentStack';
import { WebTour } from '@/features/onboarding/WebTour';
import { SheetProvider } from '@/components/ui/Sheet';
import { useMock } from '@/dev/mockState';
import { CursorGlow, Grain } from './Ambient';
import { OfflineBanner, RouteBoundary } from './RouteBoundary';

const DevPanel = import.meta.env.DEV ? lazy(() => import('@/dev/DevPanel')) : null;
import { Drawer } from './Drawer';
import { TopBar } from './TopBar';

/** Close overlays and scroll to top when the page (pathname) changes. */
function RouteEffects({ setNavOpen }: { setNavOpen: (v: boolean) => void }) {
  const { pathname } = useLocation();
  const { resetBar } = useOrca();
  const { hideNews } = useNewsSheet();
  useBodyClass('on-ai', pathname === '/ai');
  useStillPage(pathname === '/ai');
  useEffect(() => { syncChromeColor(); }, [pathname]);
  useEffect(() => {
    setNavOpen(false);
    resetBar();
    hideNews();
    window.scrollTo(0, 0);
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

export function AppShell() {
  useVisualViewportController();
  const [navOpen, setNavOpen] = useState(false);
  const { pathname } = useLocation();
  const mock = useMock();
  const top = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = top.current;
    if (!el) return undefined;
    const sync = () => document.documentElement.style.setProperty('--app-top-h', `${el.offsetHeight}px`);
    const observer = new ResizeObserver(sync);
    observer.observe(el);
    sync();
    return () => observer.disconnect();
  }, []);
  return (
    <ShellCtx.Provider value={{ navOpen, setNavOpen }}>
      <LanguageGate>
      <OrcaProvider>
        <NewsSheetProvider>
          <NoticesProvider>
            <SheetProvider>
              <div className="app-shell">
                <Grain />
                <CursorGlow />
                <RouteEffects setNavOpen={setNavOpen} />
                <div className="app-top-shell" ref={top}><TopBar /></div>
                <RouteBoundary path={pathname}><Suspense fallback={null}><Outlet /></Suspense></RouteBoundary>
                <Drawer />
                <AskBar />
                <UrgentStack />
                {/* inside the router: the tour opens real routes without a reload */}
                <Suspense fallback={null}><WebTour /></Suspense>
                <OfflineBanner forced={mock.data === 'offline'} />
                {DevPanel && <Suspense fallback={null}><DevPanel /></Suspense>}
              </div>
            </SheetProvider>
          </NoticesProvider>
        </NewsSheetProvider>
      </OrcaProvider>
      </LanguageGate>
    </ShellCtx.Provider>
  );
}
